import { attendanceBands, type PackageFormData, type Step } from "./types";
import type { InquiryProfileConfig } from "./inquiry-profile-schema";
import { activeQuestions } from "./active-questions";
import styles from "./EventSummaryPanel.module.css";

/**
 * The "Your event" summary panel — the two-pane quote-builder's right rail
 * on desktop. One labeled row per answer group, each jumping back to the
 * step that owns it. Unanswered rows show a muted placeholder so the panel
 * reads as a build-in-progress, not an error. The estimate is not here:
 * it is asked for once, on the review step, because it is a model call.
 */

interface SummaryRow {
  key: string;
  label: string;
  value: string | null;
  placeholder: string;
  step: Step;
}

function formatSummaryDate(iso: string): string {
  const date = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat("en-NZ", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
}

export function buildSummaryRows(
  formData: Partial<PackageFormData>,
  profile: InquiryProfileConfig,
  riderAttached: boolean,
): SummaryRow[] {
  const eventTypeLabel =
    formData.eventType === "other" && formData.eventTypeOther
      ? formData.eventTypeOther
      : (profile.eventDetailsStep.eventTypeOptions.find(
          (t) => t.value === formData.eventType,
        )?.label ?? null);

  let dateLabel: string | null = null;
  if (formData.eventDate) {
    dateLabel = formatSummaryDate(formData.eventDate);
    if (formData.eventEndDate && formData.eventEndDate !== formData.eventDate) {
      dateLabel += ` – ${formatSummaryDate(formData.eventEndDate)}`;
    }
  }

  let guestsLabel: string | null = null;
  if (formData.attendance !== undefined) {
    guestsLabel = `${formData.attendance} guests`;
  } else if (formData.attendanceBand) {
    guestsLabel =
      attendanceBands.find(({ value }) => value === formData.attendanceBand)
        ?.label ?? null;
  }

  const venueParts = [formData.indoorOutdoor, formData.location].filter(
    (part): part is string => Boolean(part),
  );

  // The content row summarises the profile's own questions, the ones
  // asked right now: a select's answer is the value ("6m x 4m"), a
  // ticked checkbox is its label ("Live Band(s)"). Text/textarea answers
  // are too long to chip.
  const asked = activeQuestions(profile, formData, riderAttached);
  const contentParts = asked
    .map((q) => {
      const answer = formData.profileAnswers?.[q.id];
      if (q.type === "select") {
        return typeof answer === "string" && answer ? answer : null;
      }
      if (q.type === "checkbox") return answer === true ? q.label : null;
      return null;
    })
    .filter((part): part is string => part !== null);
  const contentLabel = profile.contentStep.progressLabel ?? "Sound";
  const contentJoiner = " · ";

  return [
    {
      key: "event",
      label: "Event",
      value: eventTypeLabel,
      placeholder: "Add your event type",
      step: "event-details",
    },
    {
      key: "date",
      label: "Date",
      value: dateLabel,
      placeholder: "Add your date",
      step: "event-details",
    },
    {
      key: "guests",
      label: "Guests",
      value: guestsLabel,
      placeholder: "Add guest numbers",
      step: "venue",
    },
    {
      key: "venue",
      label: "Venue",
      value: venueParts.length > 0 ? venueParts.join(" · ") : null,
      placeholder: "Add your venue",
      step: "venue",
    },
    {
      key: "sound",
      label: contentLabel,
      value: contentParts.length > 0 ? contentParts.join(contentJoiner) : null,
      placeholder: "Add what's happening",
      step: "content",
    },
  ];
}

function SummaryRows({
  rows,
  currentStep,
  onJump,
}: {
  rows: readonly SummaryRow[];
  currentStep: Step;
  onJump: (step: Step) => void;
}): React.JSX.Element {
  return (
    <div className={styles.rows}>
      {rows.map((row) => (
        <button
          key={row.key}
          type="button"
          onClick={() => onJump(row.step)}
          disabled={row.step === currentStep}
          className={styles.row}
        >
          <span className={styles.rowLabel}>{row.label}</span>
          <span
            className={`${styles.rowValue} ${
              row.value ? styles.rowValueAnswered : ""
            }`}
          >
            {row.value ?? row.placeholder}
          </span>
        </button>
      ))}
    </div>
  );
}

/** Desktop right rail. Borderless: the shell's rail carries the hairline
 *  separator, not the panel. Drawn only once something is answered. */
export function EventSummaryPanel({
  rows,
  currentStep,
  onJump,
}: {
  rows: readonly SummaryRow[];
  currentStep: Step;
  onJump: (step: Step) => void;
}): React.JSX.Element {
  return (
    <div>
      <h2 className={styles.rowsHeading}>Your event</h2>
      <SummaryRows
        rows={rows}
        currentStep={currentStep}
        onJump={onJump}
      />
    </div>
  );
}
